'use client';

import { useMachine } from '@xstate/react';
import { useCallback, useEffect } from 'react';
import { cartStateMachine, cartHelpers, cartSelectors } from '../state-machines/cartStateMachine';
import type { CartSaved } from '@/types/manage.types';

/**
 * Cart persistence interface
 */
interface CartPersistence {
  load: () => Promise<CartSaved[]>;
  save: (items: CartSaved[]) => Promise<void>;
}

/**
 * Default localStorage implementation
 */
const defaultPersistence: CartPersistence = {
  load: async () => {
    try {
      const stored = localStorage.getItem('cart');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load cart from storage:', error);
      return [];
    }
  },

  save: async (items: CartSaved[]) => {
    try {
      localStorage.setItem('cart', JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save cart to storage:', error);
      throw error;
    }
  },
};

/**
 * React hook for managing cart with state machine
 * Provides optimistic updates, offline support, and automatic persistence
 */
export function useCartManagement(persistence: CartPersistence = defaultPersistence) {
  const [state, send] = useMachine(cartStateMachine);

  // Load cart from storage on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        const items = await persistence.load();
        send({ type: 'LOAD_FROM_STORAGE', items });
      } catch (error) {
        console.error('Failed to load cart from storage:', error);
        // Still transition to idle even if loading fails
        send({ type: 'LOAD_FROM_STORAGE', items: [] });
      }
    };

    loadCart();
  }, [send, persistence]);

  // Network status detection
  useEffect(() => {
    const handleOnline = () => send({ type: 'NETWORK_ONLINE' });
    const handleOffline = () => send({ type: 'NETWORK_OFFLINE' });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [send]);

  // Auto-sync on visibility change (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && cartSelectors.canSync(state)) {
        send({ type: 'SYNC_TO_STORAGE' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [send, state]);

  // Actions
  const addItem = useCallback(
    (
      item: Omit<CartSaved, 'in_cart'> & {
        note?: string;
        price?: number;
        attributes?: Array<{ key: string; value: string; label: string }>;
      },
    ) => {
      send({ type: 'ADD_ITEM', item });
    },
    [send],
  );

  const updateItem = useCallback(
    (variant_id: string, updates: Partial<CartSaved>) => {
      send({ type: 'UPDATE_ITEM', variant_id, updates });
    },
    [send],
  );

  const removeItem = useCallback(
    (variant_id: string) => {
      send({ type: 'REMOVE_ITEM', variant_id });
    },
    [send],
  );

  const clearCart = useCallback(() => {
    send({ type: 'CLEAR_CART' });
  }, [send]);

  const syncToStorage = useCallback(() => {
    send({ type: 'SYNC_TO_STORAGE' });
  }, [send]);

  // Auto-save when cart items change
  useEffect(() => {
    const saveCart = async () => {
      if (!cartSelectors.isDirty(state)) return;

      try {
        await persistence.save(state.context.items);
        send({ type: 'SYNC_SUCCESS' });
      } catch (error) {
        console.error('Failed to save cart:', error);
        send({ type: 'SYNC_FAILED', error: error instanceof Error ? error.message : 'Save failed' });
      }
    };

    // Debounce saves to avoid too many writes
    const timeoutId = setTimeout(saveCart, 1000);
    return () => clearTimeout(timeoutId);
  }, [state.context.items, state, send, persistence]);

  const retrySync = useCallback(() => {
    send({ type: 'RETRY_SYNC' });
  }, [send]);

  const undoOptimistic = useCallback(
    (operationId: string) => {
      send({ type: 'UNDO_OPTIMISTIC', operationId });
    },
    [send],
  );

  // Advanced operations
  const updateItemQuantity = useCallback(
    (variant_id: string, quantity: number) => {
      if (quantity <= 0) {
        removeItem(variant_id);
      } else {
        updateItem(variant_id, { quantity });
      }
    },
    [updateItem, removeItem],
  );

  const incrementItem = useCallback(
    (variant_id: string) => {
      const item = cartHelpers.findItemByVariantId(state.context.items, variant_id);
      if (item) {
        updateItemQuantity(variant_id, (item.quantity || 1) + 1);
      }
    },
    [state.context.items, updateItemQuantity],
  );

  const decrementItem = useCallback(
    (variant_id: string) => {
      const item = cartHelpers.findItemByVariantId(state.context.items, variant_id);
      if (item) {
        updateItemQuantity(variant_id, (item.quantity || 1) - 1);
      }
    },
    [state.context.items, updateItemQuantity],
  );

  const addOrUpdateItem = useCallback(
    (
      item: Omit<CartSaved, 'in_cart'> & {
        note?: string;
        price?: number;
        attributes?: Array<{ key: string; value: string; label: string }>;
      },
    ) => {
      const existingItem = cartHelpers.findItemByVariantId(state.context.items, item.variant_id);

      if (existingItem) {
        updateItemQuantity(item.variant_id, (existingItem.quantity || 1) + (item.quantity || 1));
      } else {
        addItem(item);
      }
    },
    [state.context.items, addItem, updateItemQuantity],
  );

  // Computed values
  const itemCount = cartHelpers.getItemCount(state.context.items);
  const total = cartHelpers.calculateTotal(state.context.items);
  const validation = cartHelpers.validateCartItems(state.context.items);

  // Selectors
  const selectors = {
    isLoading: cartSelectors.isLoading(state),
    isIdle: cartSelectors.isIdle(state),
    isSyncing: cartSelectors.isSyncing(state),
    isSyncFailed: cartSelectors.isSyncFailed(state),
    isOffline: cartSelectors.isOffline(state),
    isOptimistic: cartSelectors.isOptimistic(state),

    hasItems: cartSelectors.hasItems(state),
    isEmpty: cartSelectors.isEmpty(state),
    isDirty: cartSelectors.isDirty(state),
    hasError: cartSelectors.hasError(state),
    hasPendingOperations: cartSelectors.hasPendingOperations(state),

    canSync: cartSelectors.canSync(state),
    canRetry: cartSelectors.canRetry(state),
  };

  return {
    // State
    state: state.value,
    context: state.context,

    // Items
    items: state.context.items,
    itemCount,
    total,
    validation,

    // Selectors
    ...selectors,

    // Basic actions
    addItem,
    updateItem,
    removeItem,
    clearCart,

    // Advanced actions
    updateItemQuantity,
    incrementItem,
    decrementItem,
    addOrUpdateItem,

    // Sync actions
    syncToStorage,
    retrySync,
    undoOptimistic,

    // Helpers
    findItem: (variant_id: string) => cartHelpers.findItemByVariantId(state.context.items, variant_id),

    // Raw state for advanced usage
    machineState: state,
    send,
  };
}

/**
 * Hook for cart summary with formatted values
 */
export function useCartSummary() {
  const cart = useCartManagement();

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getSyncStatusMessage = (): string => {
    if (cart.isSyncing) return 'Syncing...';
    if (cart.isSyncFailed) return 'Sync failed';
    if (cart.isOffline) return 'Offline';
    if (cart.isDirty) return 'Unsaved changes';
    if (cart.hasPendingOperations) return 'Pending operations';
    return 'Synced';
  };

  const getSyncStatusType = (): 'synced' | 'syncing' | 'error' | 'offline' | 'dirty' => {
    if (cart.isSyncing) return 'syncing';
    if (cart.isSyncFailed) return 'error';
    if (cart.isOffline) return 'offline';
    if (cart.isDirty || cart.hasPendingOperations) return 'dirty';
    return 'synced';
  };

  return {
    ...cart,

    // Formatted values
    formattedTotal: formatPrice(cart.total),

    // Status information
    syncStatusMessage: getSyncStatusMessage(),
    syncStatusType: getSyncStatusType(),

    // Summary stats
    summary: {
      itemCount: cart.itemCount,
      total: cart.total,
      formattedTotal: formatPrice(cart.total),
      isEmpty: cart.isEmpty,
      isValid: cart.validation.isValid,
      validationErrors: cart.validation.errors,
    },
  };
}

/**
 * Hook for cart persistence with custom storage backend
 */
export function useCartPersistence(loadFn: () => Promise<CartSaved[]>, saveFn: (items: CartSaved[]) => Promise<void>) {
  const persistence: CartPersistence = {
    load: loadFn,
    save: saveFn,
  };

  return useCartManagement(persistence);
}
